import React, { useCallback, useEffect, useRef, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import ModalBottomSheet from '@zet/common/components/modalBottomSheet'
import {
  B1,
  B2,
  B5,
  Button,
  H4,
  Icon,
  Input,
  InputGroup,
  Pressable,
  SH3,
  Stack,
  colors,
} from '@zet/design-system'
import CheckBox from '@zet/design-system/svg/CheckBox'
import CheckBoxTickFilled from '@zet/design-system/svg/CheckBoxTickFilled'
import { i18n } from '@zet/localization'
import { isAndroid, isIOS } from '@zet/utils'
import { PLACE_HOLDER_PHONE_NUMBER } from '@zet/utils/Constants'
import { NAME_REGEX } from '@zet/utils/regularExpression'
import {
  MOBILE_NUMBER_VALIDATION,
  NAME_FIELD_EMPTY,
  NAME_FIELD_VALIDATION,
} from '@zet/utils/validationMessage'
import * as Application from 'expo-application'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { checkReferralZetCode } from '../api/functions'
import {
  useRegisterUser,
  useVerifyOtp,
  useVerifyPhoneNumber,
} from '../api/hooks'
import IdCard from '../assets/svg/idCard.svg'
import { appendOneCode, updateZetCode } from '../utils/index'
import { VerifyOTPBottomSheet } from './verifyOTP'

type IRegisterUser = {
  isVisible: boolean
  setIsVisible: (x: boolean) => void
  handleOnPress: () => void
}

const registerUserSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(1, { message: NAME_FIELD_EMPTY })
    .regex(new RegExp(NAME_REGEX), NAME_FIELD_VALIDATION),
  mobileNumber: z
    .string()
    .min(1, { message: NAME_FIELD_EMPTY })
    .regex(new RegExp('.*^[6-9][0-9]{9}.*'), MOBILE_NUMBER_VALIDATION)
    .max(10, { message: MOBILE_NUMBER_VALIDATION }),
  referralCode: z.string().trim().optional(),
})

export function RegisterUser({
  isVisible,
  setIsVisible,
  handleOnPress,
}: IRegisterUser) {
  const [isOTPVisible, setOTPVisible] = useState(false)
  const [showReferralVisible, setShowReferralVisible] = useState(false)
  type RegisterFormType = z.infer<typeof registerUserSchema>
  const {
    control,
    setError,
    handleSubmit,
    clearErrors,
    getValues,
    setValue,
    formState: { errors, isValid },
  } = useForm<RegisterFormType>({
    mode: 'onChange',
    resolver: zodResolver(registerUserSchema),
  })
  const inputRef = useRef(null)
  const { mutate, isLoading } = useRegisterUser()
  const queryClient = useQueryClient()
  const verifyOTP = useCallback(useVerifyOtp, [])

  const { mutate: verifyOtp, isLoading: verifyingOtp } = verifyOTP()

  const getVerifyPhoneNumber = useCallback(useVerifyPhoneNumber, [])
  const { mutate: mutateVerifyPhoneNum } = getVerifyPhoneNumber()

  const handleResetOtp = (phoneNumber: string) => {
    mutateVerifyPhoneNum({
      phoneNumber: Number(phoneNumber?.slice(2)) || 0,
      utmAttributes: {},
    })
  }

  const handleSubmitOtp = (otp: string, phoneNumber: string) => {
    verifyOtp(
      { mobileNumber: phoneNumber, value: otp },
      {
        onSuccess: () => {
          setIsVisible(false)
          handleOnPress()
          queryClient.invalidateQueries({ queryKey: ['home-widgets'] })
          queryClient.invalidateQueries({ queryKey: ['userContext'] })
        },
      },
    )
  }
  const onSubmit = (data: any) => {
    const referralCode = appendOneCode(data?.referralCode)
    mutate(
      {
        phoneNumber: data.mobileNumber ? Number(data.mobileNumber) : 0,
        fullName: data.fullName,
        referralCode: referralCode,
        utmAttributes: {},
      },
      {
        onSuccess: () => {
          setIsVisible(false)
          setOTPVisible(true)
          queryClient.invalidateQueries({ queryKey: ['home-widgets'] })
          queryClient.invalidateQueries({ queryKey: ['userContext'] })
        },
      },
    )
  }

  useEffect(() => {
    extractReferalCode()
  }, [])

  const extractReferalCode = async () => {
    let res: string | undefined

    if (isAndroid) {
      res = await Application.getInstallReferrerAsync()
    } else if (isIOS) {
      return { referrer: '', referralCode: '', referralSegment: '' }
    }

    if (!res) return

    const params = res.split('&')
    const utmParams = params?.filter((p) => p.includes('onecode_referral_code'))
    let referralCode = ''

    if (utmParams.length > 0) {
      referralCode = utmParams[0].split('=')[1]
      setValue('referralCode', updateZetCode(referralCode))
      setShowReferralVisible(true)
    }
  }

  return (
    <>
      <ModalBottomSheet
        visible={isVisible}
        onDismiss={() => setIsVisible(false)}
        sheetName={'create_your_verified_profile'}
      >
        <Stack className="items-center">
          <IdCard className="mt-5" />
          <H4 className="mt-3 text-center">
            {i18n.t('registerUser.completeRegister')}
          </H4>
          <SH3 className="text-grays-60 mt-2 text-center">
            {i18n.t('registerUser.basicDetails')}
          </SH3>
        </Stack>

        <Stack className="mb-3 mt-3 px-3">
          <B2 className="text-grays-80 mb-1">
            {i18n.t('visitingCard.yourName')}

            <B1 className="text-error-100 ml-1">*</B1>
          </B2>
          <Controller
            control={control}
            render={({
              field: { onChange, onBlur, value },
              fieldState: { isTouched },
            }) => (
              <Input
                placeholder={i18n.t('visitingCard.placeName')}
                onBlur={onBlur}
                onChangeText={onChange}
                spellCheck={false}
                value={value}
                autoCapitalize="words"
                type={
                  errors?.fullName?.message && isTouched ? 'error' : 'primary'
                }
              />
            )}
            name="fullName"
          />
          <B5 className="text-grays-70 mt-2">
            {i18n.t('visitingCard.nameMentioned')}
          </B5>
          {errors.fullName && (
            <B5 style={{ color: colors.error[150] }}>
              {errors.fullName.message}
            </B5>
          )}
        </Stack>
        <Stack className="mb-3 px-3">
          <B2 className=" text-grays-80 mb-1 ml-1">
            {i18n.t('visitingCard.yourPhoneNumber')}

            <B1 className="text-error-100 ml-1">*</B1>
          </B2>

          <Controller
            control={control}
            render={({ field: { onChange, onBlur, value } }) => (
              <InputGroup
                placeholder={PLACE_HOLDER_PHONE_NUMBER}
                onBlur={() => {
                  onBlur()
                }}
                onChangeText={(value) => {
                  onChange(value)
                  if (value.length === 10) {
                    inputRef.current.blur()
                  }
                }}
                value={value}
                type={`${errors.mobileNumber?.message ? 'error' : 'primary'}`}
                size="md"
                maxLength={10}
                keyboardType="number-pad"
                ref={inputRef}
              />
            )}
            name="mobileNumber"
          />
          {errors.mobileNumber?.message ? (
            <Stack className="mt-2">
              <B5 className="text-error-100">{errors.mobileNumber?.message}</B5>
            </Stack>
          ) : null}
        </Stack>

        <Stack className="mb-2 px-3">
          <Pressable
            className="flex-row items-center"
            onPress={() => setShowReferralVisible(!showReferralVisible)}
            eventName="click_action"
            eventPayload={{
              item_name: 'referral_check_box_create_profile',
            }}
          >
            {showReferralVisible ? (
              <CheckBoxTickFilled color={colors.primary[150]} size={18} />
            ) : (
              <CheckBox color={colors.neutral.black100} size={18} />
            )}
            <B2 className="text-grays-80 ml-1">
              {i18n.t('visitingCard.referralCode')}
            </B2>
          </Pressable>

          {showReferralVisible && (
            <Stack className="mt-2">
              <B2 className="text-grays-80">
                {i18n.t('agentId.haveReferral')}
              </B2>
              <Controller
                control={control}
                rules={{ required: showReferralVisible }}
                render={({
                  field: { onChange, onBlur, value },
                  fieldState: { isTouched },
                }) => (
                  <Input
                    placeholder="Eg: varun12345"
                    className="mt-2"
                    onChangeText={(value) => {
                      onChange(value)
                      checkReferralZetCode(value).then((value) => {
                        !value
                          ? setError('referralCode', {
                              message: 'agentId.invalidId',
                            })
                          : clearErrors('referralCode')
                      })
                    }}
                    type={errors?.referralCode?.message ? 'error' : 'primary'}
                    value={updateZetCode(value)}
                    iconRight={
                      getValues('referralCode') && (
                        <Icon
                          className="mr-1"
                          color={
                            errors.referralCode
                              ? colors.error[100]
                              : colors.success[100]
                          }
                          type={errors.referralCode ? 'ErrorOutlined' : 'Tick'}
                        />
                      )
                    }
                  />
                )}
                name="referralCode"
              />
              {errors.referralCode && (
                <B5 style={{ color: colors.error[150] }}>
                  {i18n.t('agentId.invalidId')}
                </B5>
              )}
            </Stack>
          )}
        </Stack>
        <Stack className="border-grays-40 mt-1 w-full border-t p-3">
          <Button
            size={'lg'}
            className="w-full"
            disabled={
              !isValid || (showReferralVisible && !getValues('referralCode'))
            }
            loading={isLoading}
            onPress={handleSubmit(onSubmit)}
            eventName="click_action"
            eventPayload={{
              item_name: 'create_profile',
            }}
          >
            {i18n.t('registerUser.createProfile')}
          </Button>
        </Stack>
      </ModalBottomSheet>
      <VerifyOTPBottomSheet
        mobileNumber={`91${getValues('mobileNumber')}`}
        isVisible={isOTPVisible}
        setIsVisible={setOTPVisible}
        handleResetOtp={handleResetOtp}
        handleSubmit={handleSubmitOtp}
        isVerifying={verifyingOtp}
        heading={i18n.t('otpInput.enter')}
      />
    </>
  )
}
